
import React from 'react';
import { Button } from "@/components/ui/button";
import { PredictionCard } from "@/components/predictions/PredictionCard";
import { PredictionChart } from "@/components/predictions/PredictionChart";
import { AIPrediction } from "@/services/predictions/predictionsService";
import { getChartDataFromCache } from "@/lib/predictions/chartCache";
import { mockGoldChartData, mockSilverChartData } from "./mockChartData";
import ReactMarkdown from 'react-markdown';
import { AlertTriangle } from 'lucide-react';

interface PredictionsListProps {
  predictions: AIPrediction[];
  markdownContent: string | null;
  onGenerate: () => void;
  isGenerating: boolean;
  canGenerate?: boolean;
}

export function PredictionsList({ predictions, markdownContent, onGenerate, isGenerating, canGenerate }: PredictionsListProps) {
  console.log('[PredictionsList] 📋 Rendering with predictions:', predictions.length);
  
  const hasPredictions = Array.isArray(predictions) && predictions.length > 0;
  
  // Ottieni i dati reali dei grafici dalla cache
  const chartData = getChartDataFromCache();
  const hasChartData = chartData.gold || chartData.silver;
  
  console.log('[PredictionsList] 📊 Chart data status:', {
    hasRealChartData: hasChartData,
    goldData: chartData.gold ? 'REAL DATA' : 'NULL',
    silverData: chartData.silver ? 'REAL DATA' : 'NULL',
    willUseMockData: !hasChartData
  });
  
  // Sort predictions to show ORO first
  const sortedPredictions = [...predictions].sort((a, b) => {
    if (a.metal === 'ORO') return -1;
    if (b.metal === 'ORO') return 1;
    return 0;
  });
  
  return (
    <div className="space-y-8">
      {markdownContent && (
        <div className="col-span-full mb-6 p-6 bg-card rounded-lg border shadow-sm">
          <div className="prose dark:prose-invert max-w-none">
            <ReactMarkdown>{markdownContent}</ReactMarkdown>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 auto-rows-fr">
        {hasPredictions ? (
          sortedPredictions.map((prediction) => (
            <PredictionCard key={prediction.id} prediction={prediction} />
          ))
        ) : (
          <div className="col-span-full text-center py-6">
            <p className="text-muted-foreground">Nessuna previsione disponibile. Genera nuove previsioni.</p>
          </div>
        )}
      </div>
      
      {/* Grafici delle previsioni con dati reali o fallback ai mock */}
      {hasPredictions && (
        <div className="space-y-8">
          <div className="border-t pt-8">
            <h2 className="text-2xl font-bold mb-6 text-center">
              Analisi Grafica delle Previsioni
            </h2>
            
            
            <div className="space-y-10">
              {/* Grafico Oro - usa dati reali se disponibili, altrimenti mock */}
              <PredictionChart data={chartData.gold || mockGoldChartData} />
              
              {/* Grafico Argento - presente se ci sono previsioni per l'argento */}
              {sortedPredictions.some(p => p.metal === 'ARGENTO') && (
                <PredictionChart data={chartData.silver || mockSilverChartData} />
              )}
            </div>
          </div>
          
          {/* Card Disclaimer */}
          <div className="metal-card p-6 border-l-4 border-l-amber-500">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <h3 className="text-lg font-semibold text-foreground">
                Disclaimer Importante
              </h3>
            </div>
            
            <div className="prose prose-sm dark:prose-invert max-w-none text-muted-foreground">
              <p className="mb-3">
                <strong>Le previsioni presentate hanno esclusivamente finalità informative e divulgative.</strong> 
                Gildy non fornisce consulenza finanziaria, patrimoniale o legale.
              </p>
              
              <p className="mb-3">
                Le previsioni generate tramite intelligenza artificiale sono fornite unicamente a scopo 
                informativo e non costituiscono in alcun modo un invito, una sollecitazione o un consiglio 
                a vendere, acquistare o detenere metalli preziosi.
              </p>
              
              <p className="mb-0">
                <strong>Gildy non si assume alcuna responsabilità</strong> per l'utilizzo delle informazioni 
                fornite nelle previsioni né per le eventuali decisioni prese sulla base di tali informazioni.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {canGenerate && (
        <div className="flex justify-center mt-6">
          <Button 
            onClick={onGenerate} 
            variant="outline" 
            disabled={isGenerating}
            className="min-w-40"
          >
            {isGenerating ? "Generazione in corso..." : "Genera nuove previsioni"}
          </Button>
        </div>
      )}
    </div>
  );
}
